<template>
  <div class="feature-tile-row-wrap">
    <h2 v-if="settings.title" class="row-title">{{ settings.title }}</h2>

    <v-row :dense="dense">
      <v-col
        v-for="(t, i) in resolvedTiles"
        :key="t.id || t.key || i"
        cols="12"
        sm="6"
        :md="t.tile_cols || colsMd"
      >
        <div class="tile-slot">
          <div class="tile-edit-actions" v-if="editMode">
            <v-btn icon small title="Tile Settings" @click="$emit('editTile', t)">
              <v-icon small>settings</v-icon>
            </v-btn>
            <v-btn icon small title="Delete Tile" @click="$emit('deleteTile', t)">
              <v-icon small>delete</v-icon>
            </v-btn>
            <v-btn icon small title="Copy Tile" @click="$emit('copyTile', t)">
              <v-icon small>content_copy</v-icon>
            </v-btn>
          </div>
          <cFeatureTile
            :definition="t"
            :edit-mode="editMode"
            :qa-actions="qaActions"
            @click="$emit('tileClick', t)"
            @addDocument="$emit('addDocument', $event)"
          />
        </div>
      </v-col>

      <v-col v-if="editMode" cols="12" sm="6" :md="colsMd">
        <v-btn block outlined class="add-tile-btn" @click="$emit('addTile', settings)">
          <v-icon left>add</v-icon>Add Tile
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import cFeatureTile from "./cFeatureTile.vue";

/**
 * A row of pastel feature tiles — suitable as a landing-page control type
 * (controlType: 'FeatureTiles') or for direct use on a static page.
 *
 * Landing-config shape (definition):
 *   {
 *     controlType: 'FeatureTiles',
 *     title: string,
 *     links: [  // same naming as other landing controls
 *       { title, description, icon, color, iconColor, url, type, tile_cols, open_newtab, createDocType }
 *     ]
 *   }
 *
 * Standalone shape: pass `tiles` directly.
 */
export default {
  name: "cFeatureTileRow",
  components: { cFeatureTile },
  props: {
    // landing-config mode
    definition: { type: Object, default: null },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
    // standalone mode
    tiles: { type: Array, default: null },
    title: { type: String, default: "" },
    colsMd: { type: [String, Number], default: 3 },
    dense: { type: Boolean, default: false },
  },
  computed: {
    settings() {
      const d = this.definition || {};
      return {
        title: d.title ?? this.title,
        links: d.links ?? null,
      };
    },
    resolvedTiles() {
      // Landing-config mode: `links` is authoritative. An empty array is
      // valid and must NOT fall back to demo defaults (would duplicate tiles).
      if (this.definition) return this.settings.links || [];
      // Standalone mode: explicit tiles win; otherwise show built-in defaults.
      if (this.tiles && this.tiles.length) return this.tiles;
      return this.defaultTiles;
    },
    defaultTiles() {
      return [
        {
          key: "roleCreate",
          title: "RoleCreate",
          description:
            "Create, manage and govern your job profiles and local job position descriptions.",
          icon: "work_outline",
          color: "blue",
        },
        {
          key: "roleArchitect",
          title: "RoleArchitect",
          description:
            "Create, manage and govern your job architecture, job family structures, job titling, levelling & grading framework.",
          icon: "account_tree",
          color: "yellow",
        },
        {
          key: "roleSkill",
          title: "RoleSkill",
          description:
            "Create, manage and govern your skills and competencies across all your jobs and job architecture.",
          icon: "psychology",
          color: "pink",
        },
        {
          key: "roleEvaluate",
          title: "RoleEvaluate",
          description:
            "Define and manage the levels of your jobs and visualise how they sit in your levelling framework.",
          icon: "playlist_add_check",
          color: "orange",
        },
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.feature-tile-row-wrap {
  font-family: 'DM Sans', sans-serif !important;
  ::v-deep *:not(.v-icon):not(.v-icon *) { font-family: inherit !important; }
  h1, h2, ::v-deep h1, ::v-deep h2 {
    font-family: 'DM Serif Display', serif !important;
  }

  .row-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 12px 0;
  }
}

.tile-slot {
  position: relative;

  .tile-edit-actions {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    flex-direction: row;
    z-index: 2;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 16px;
  }
}

.add-tile-btn {
  height: 100% !important;
  min-height: 120px;
  border-radius: 20px !important;
  border-style: dashed !important;
}
</style>
